// Katalog der Vorbelegungs-Quellen und die reine Abbildung Datensatz → Werte.
// Ohne Supabase-Import, damit die Regeln (u. a. der Vorrang bei der
// Gläubiger-ID) testbar bleiben; die Datenbeschaffung liegt in vorbelegung.ts.
//
// ACHTUNG: Die Liste der Quellen-Schlüssel wird in
// supabase/functions/signieren/index.ts (QUELLEN_KATALOG) als Whitelist
// gespiegelt. Beide müssen identisch bleiben — quellen.drift.test.ts wacht darüber.

export const QUELLEN: { wert: string; label: string }[] = [
  { wert: 'mieter.name', label: 'Mieter — Name (komplett)' },
  { wert: 'mieter.vorname', label: 'Mieter — Vorname' },
  { wert: 'mieter.nachname', label: 'Mieter — Nachname' },
  { wert: 'mieter.email', label: 'Mieter — E-Mail' },
  { wert: 'mieter.telefon', label: 'Mieter — Telefon' },
  { wert: 'mieter.einheit', label: 'Mieter — Wohnung/Einheit' },
  { wert: 'mieter.adresse', label: 'Mieter — Anschrift (Wohnadresse)' },
  { wert: 'mieter.iban', label: 'Mieter — IBAN' },
  { wert: 'mieter.mandatsreferenz', label: 'Mieter — SEPA-Mandatsreferenz' },
  { wert: 'mieter.kundennummer', label: 'Mieter — Kunden-/Objektnummer' },
  { wert: 'eigentuemer.name', label: 'Eigentümer — Name (komplett)' },
  { wert: 'eigentuemer.vorname', label: 'Eigentümer — Vorname' },
  { wert: 'eigentuemer.nachname', label: 'Eigentümer — Nachname' },
  { wert: 'eigentuemer.email', label: 'Eigentümer — E-Mail' },
  { wert: 'eigentuemer.adresse', label: 'Eigentümer — Anschrift' },
  { wert: 'eigentuemer.glaeubiger_id', label: 'Eigentümer — Gläubiger-ID (sonst zentrale)' },
  { wert: 'eigentuemer.mandatsreferenz', label: 'Eigentümer — SEPA-Mandatsreferenz' },
  { wert: 'eigentuemer.kundennummer', label: 'Eigentümer — Kunden-/Objektnummer' },
  { wert: 'kontakt.name', label: 'Kontakt — Name/Firma' },
  { wert: 'kontakt.firma', label: 'Kontakt — Firmenname' },
  { wert: 'kontakt.email', label: 'Kontakt — E-Mail' },
  { wert: 'kontakt.adresse', label: 'Kontakt — Anschrift' },
  { wert: 'kontakt.glaeubiger_id', label: 'Kontakt — Gläubiger-ID' },
  { wert: 'kontakt.iban', label: 'Kontakt — IBAN' },
  { wert: 'kontakt.bic', label: 'Kontakt — BIC' },
  { wert: 'kontakt.kontoinhaber', label: 'Kontakt — Kontoinhaber' },
  { wert: 'objekt.adresse', label: 'Objekt — Adresse' },
  { wert: 'objekt.bezeichnung', label: 'Objekt — Bezeichnung' },
  { wert: 'einheit.bezeichnung', label: 'Einheit — Bezeichnung' },
  { wert: 'verwaltung.glaeubiger_id', label: 'Verwaltung — Gläubiger-ID (zentral)' },
  { wert: 'datum.heute', label: 'Heutiges Datum' },
];

/** Alle gültigen Quellen-Schlüssel — Grundlage der Server-Whitelist. */
export const QUELLEN_SCHLUESSEL = QUELLEN.map((q) => q.wert);

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Setzt einen Wert nur, wenn er nicht leer ist (leere Felder bleiben leer). */
const setze = (werte: Record<string, string>, schluessel: string, wert: unknown) => {
  const v = text(wert);
  if (v) werte[schluessel] = v;
};

/** Anschrift aus Straße/Hausnummer/PLZ/Ort — leere Teile fallen weg. */
export function baueAdresse(q: { strasse?: unknown; hausnummer?: unknown; plz?: unknown; stadt?: unknown }): string {
  const strasse = `${text(q.strasse)} ${text(q.hausnummer)}`.trim();
  const ort = `${text(q.plz)} ${text(q.stadt)}`.trim();
  return [strasse, ort].filter(Boolean).join(', ');
}

/**
 * Gläubiger-ID des Eigentümers, sonst die zentrale der Verwaltung.
 * So muss nur dort gepflegt werden, wo sie abweicht.
 */
export function waehleGlaeubigerId(eigen: unknown, zentral: unknown): string {
  return text(eigen) || text(zentral);
}

export function mieterWerte(m: any): Record<string, string> {
  const werte: Record<string, string> = {};
  if (!m) return werte;
  setze(werte, 'mieter.name', `${text(m.vorname)} ${text(m.nachname)}`.trim());
  setze(werte, 'mieter.vorname', m.vorname);
  setze(werte, 'mieter.nachname', m.nachname);
  setze(werte, 'mieter.email', m.email);
  setze(werte, 'mieter.telefon', m.telefon);
  setze(werte, 'mieter.einheit', m.einheit);
  setze(werte, 'mieter.iban', m.mieter_iban);
  setze(werte, 'mieter.mandatsreferenz', m.sepa_mandatsreferenz);
  setze(werte, 'mieter.kundennummer', m.kundennummer);
  // Wohnadresse des Mieters = Adresse seines Objekts (+ Einheit)
  const adresse = m.objekt ? baueAdresse(m.objekt) : '';
  if (adresse) werte['mieter.adresse'] = text(m.einheit) ? `${adresse}, ${text(m.einheit)}` : adresse;
  return werte;
}

export function eigentuemerWerte(e: any, zentraleGlaeubigerId: string): Record<string, string> {
  const werte: Record<string, string> = {};
  if (!e) return werte;
  setze(werte, 'eigentuemer.name', text(e.firma_name) || `${text(e.vorname)} ${text(e.nachname)}`.trim());
  setze(werte, 'eigentuemer.vorname', e.vorname);
  setze(werte, 'eigentuemer.nachname', e.nachname);
  setze(werte, 'eigentuemer.email', e.email);
  setze(werte, 'eigentuemer.adresse', baueAdresse({ strasse: e.strasse, plz: e.plz, stadt: e.stadt }));
  setze(werte, 'eigentuemer.glaeubiger_id', waehleGlaeubigerId(e.glaeubiger_id, zentraleGlaeubigerId));
  setze(werte, 'eigentuemer.mandatsreferenz', e.sepa_mandatsreferenz);
  setze(werte, 'eigentuemer.kundennummer', e.kundennummer);
  return werte;
}

export function kontaktWerte(k: any): Record<string, string> {
  const werte: Record<string, string> = {};
  if (!k) return werte;
  setze(werte, 'kontakt.name', text(k.firma_name) || `${text(k.vorname)} ${text(k.nachname)}`.trim());
  setze(werte, 'kontakt.firma', k.firma_name);
  setze(werte, 'kontakt.email', k.email);
  setze(werte, 'kontakt.adresse', baueAdresse(k));
  setze(werte, 'kontakt.glaeubiger_id', k.glaeubiger_id);
  setze(werte, 'kontakt.iban', k.iban);
  setze(werte, 'kontakt.bic', k.bic);
  setze(werte, 'kontakt.kontoinhaber', k.kontoinhaber);
  return werte;
}

export function objektWerte(o: any): Record<string, string> {
  const werte: Record<string, string> = {};
  if (!o) return werte;
  setze(werte, 'objekt.adresse', baueAdresse(o));
  setze(werte, 'objekt.bezeichnung', o.bezeichnung);
  return werte;
}

/** Kontakt-Anzeigename für die Bezugs-Pille (Firma vor Person). */
export function kontaktAnzeigename(k: any): string {
  return text(k?.firma_name) || `${text(k?.vorname)} ${text(k?.nachname)}`.trim();
}

/** Heutiges Datum als TT.MM.JJJJ. */
export function datumHeute(heute: Date): string {
  return `${String(heute.getDate()).padStart(2, '0')}.${String(heute.getMonth() + 1).padStart(2, '0')}.${heute.getFullYear()}`;
}
